import {
  catchError,
  finalize,
  map,
  Observable,
  shareReplay,
  switchMap,
  UnaryFunction,
} from 'rxjs';

/**
 * Creates an Observable that runs a function inside a Tracker.autorun and automatically
 * stops the computation when the observable is unsubscribed.
 *
 * @param trackerFunction - Function to run inside Tracker.autorun that returns a value
 * @returns Observable that emits the return value of trackerFunction
 */
export function observeTracker<T>(trackerFunction: () => T): Observable<T> {
  let computation: Tracker.Computation | undefined;
  const observable = new Observable<T>((observer) => {
    computation = Tracker.autorun(() => {
      try {
        const result = trackerFunction();
        observer.next(result);
      } catch (error) {
        observer.error(error);
      }
    });
  }).pipe(
    catchError((error) => {
      if (computation && !computation.stopped) {
        computation.stop();
      }
      throw error;
    }),
    finalize(() => {
      // this code is somehow unused as the computation is always already stopped..
      if (computation && !computation.stopped) {
        computation.stop();
      }
    }),
    shareReplay(1),
  );
  return observable;
}

export function rxJsonClone<T>(): UnaryFunction<Observable<T>, Observable<T>> {
  return (source: Observable<T>) => {
    return source.pipe(map((value) => JSON.parse(JSON.stringify(value)) as T));
  };
}

/**
 * Used in RXJS pipe like "map" or switchmap
 * observable.pipe(rxMapToTracker((options) => Tracker.autorun(Users.find(options))))
 */
export function rxMapToTracker<T, R>(
  mapFunction: (args: T) => R,
): UnaryFunction<Observable<T>, Observable<R>> {
  return (source: Observable<T>) => {
    return source.pipe(
      switchMap((args) => observeTracker(() => mapFunction(args))),
    );
  };
}
